import axios, { AxiosResponse } from 'axios';
import { SignedUrlResponse } from '../../models/signedUrlResponse';
import APIS from '../apiUrl';
import { requireAuthToken } from '../auth/getAuthToken';
import { JobLog } from 'src/models/jobLog';

/**
 * Method adopted from existing SentinelMobileApp
 * This will perform the necessary Sentinel API and S3 operations to
 * create the job and upload the job log file.
 * @param logFile - The content of the log file.
 * @param organizationId - The unique identifier of the organization the job was performed for.
 * @param fileName - Name of the log file on the controller.
 * @param ctrlSerialNumber - The serial number of the controller that performed the job.
 * @param description - Additional detail on the job.
 * @param siteName - The name of the site the job was performed at.
 * @param heatExchangerModelNumber - The model number of the heat exchanger the job was performed on.
 * @returns - The response of the create log request.
 * @throws - Throws if user token is not accessible/valid.
 */
export const createJobLogAndUpload = async (
  logFile: string,
  organizationId: string,
  fileName: string,
  ctrlSerialNumber: string,
  description?: string,
  siteName?: string,
  heatExchangerModelNumber?: string,
) => {
  const name = fileName.replace(/\.log$/i, '');
  const resolvedDescription = description?.trim() || name;
  const cleanedLogData = logFile
    .replaceAll('\0', '')
    .split(/\r?\n/)
    .filter(i => !!i.trim())
    .join('\n');

  const token = await requireAuthToken();

  const signedPutResult = await createJobLogSignedUrl(token, name, 'text/plain', ctrlSerialNumber, resolvedDescription);

  const uploadResult = await uploadFileToS3('text/plain', signedPutResult.signedRequest, cleanedLogData);

  const s3 = {
    Location: signedPutResult.Location,
    Key: signedPutResult.Key,
    Bucket: signedPutResult.Bucket,
    ETag: uploadResult.headers.ETag || uploadResult.headers.etag,
  };

  const submitData: Partial<JobLog> = {
    name: name,
    version: 'log',
    serialNumber: ctrlSerialNumber,
    description: resolvedDescription,
    deviceTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    org: organizationId,
    s3,
    siteName: siteName,
    heatExchangerModelNumber: heatExchangerModelNumber,
    originalFileName: fileName,
  };

  return await axios({
    method: 'POST',
    url: `${APIS.SENTINEL_API}/api/logs/create`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: submitData,
  });
};

const createJobLogSignedUrl = async (
  token: string,
  fileName: string,
  fileType: string,
  serialNumber: string,
  description?: string,
): Promise<SignedUrlResponse> => {
  const getSignedUrlResult = await axios({
    method: 'POST',
    url: `${APIS.SENTINEL_API}/api/logs/getsigned`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {
      fileName: fileName,
      fileType: fileType,
      serialNumber: serialNumber,
      description: description,
      s3: {
        actionType: 'putObject',
      },
    },
  });

  return getSignedUrlResult.data.data;
};

const uploadFileToS3 = async (
  fileType: string,
  signedRequest: string,
  logFile: string,
): Promise<AxiosResponse<any, any>> => {
  // Put the fileType in the headers for the upload
  const options = {
    headers: {
      'Content-Type': fileType,
    },
  };

  return await axios.put(signedRequest, logFile, options);
};
